import {useCallback, useEffect, useMemo, useState} from 'react'
import {ActivityIndicator, View} from 'react-native'
import {setStringAsync} from 'expo-clipboard'
import {Trans, useLingui} from '@lingui/react/macro'
import {useNavigation} from '@react-navigation/native'
import {type NativeStackScreenProps} from '@react-navigation/native-stack'

import {
  demoPeer,
  disableDemo,
  enableDemo,
  setDemoExpired,
  setDemoRole,
  setFamilyEnabled,
  useDemoFamily,
} from '#/lib/demoFamily'
import {buildFamilyInviteMessage} from '#/lib/familyCopy'
import {roleOf} from '#/lib/roles'
import {
  type CommonNavigatorParams,
  type NavigationProp,
} from '#/lib/routes/types'
import {
  makeFamilyInviteLink,
  useCreateFamilyLinkRequest,
  useFamilyLinksQuery,
  useFamilyMemberPlaysQuery,
} from '#/state/queries/family'
import {useCapsulesByUrisQuery} from '#/state/queries/hub'
import {useProfileQuery} from '#/state/queries/profile'
import {useSession} from '#/state/session'
import {deriveLearnerSections} from '#/screens/Hub/hubPolicy'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import * as SegmentedControl from '#/components/forms/SegmentedControl'
import * as Toggle from '#/components/forms/Toggle'
import {Celebrate_Stroke2_Corner0_Rounded as CelebrateIcon} from '#/components/icons/Celebrate'
import {ChainLink_Stroke2_Corner0_Rounded as ChainLinkIcon} from '#/components/icons/ChainLink'
import {CircleInfo_Stroke2_Corner0_Rounded as CircleInfoIcon} from '#/components/icons/CircleInfo'
import {Clock_Stroke2_Corner0_Rounded as ClockIcon} from '#/components/icons/Clock'
import {QrCode_Stroke2_Corner0_Rounded as QrCodeIcon} from '#/components/icons/QrCode'
import * as Layout from '#/components/Layout'
import * as Toast from '#/components/Toast'
import {Text} from '#/components/Typography'
import {FamilyInviteCard} from './components/FamilyInviteCard'
import {EnterCodePanel, RemoveLinkButton} from './components/FamilyLinkPanels'
import {FamilyQrCode} from './components/FamilyQrCode'
import {FamilyShareDialog} from './components/FamilyShareDialog'
import {
  PasswordConfirmDialog,
  usePasswordConfirmDialog,
} from './components/PasswordConfirmDialog'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'FamilySettings'>

export function FamilySettingsScreen({}: Props) {
  const {t: l} = useLingui()
  const t = useTheme()
  const {currentAccount} = useSession()
  const navigation = useNavigation<NavigationProp>()
  const {data: profile} = useProfileQuery({did: currentAccount?.did})
  const accountType = (profile as unknown as {accountType?: string})
    ?.accountType
  const role = roleOf(accountType)
  const demo = useDemoFamily()

  const {data: links, isLoading} = useFamilyLinksQuery()
  const createRequest = useCreateFamilyLinkRequest()
  const [code, setCode] = useState<string | null>(null)
  const [codeExpiresAt, setCodeExpiresAt] = useState<string | null>(null)
  const [codeError, setCodeError] = useState<string | null>(null)
  const [showEnterCode, setShowEnterCode] = useState(false)

  const passwordDialog = usePasswordConfirmDialog()
  const shareDialogControl = Dialog.useDialogControl()

  // Demo mode lets you try the flow from either side with a simulated peer.
  const isParent = demo.enabled ? demo.role === 'parent' : role === 'parent'
  const members = isParent ? (links?.asParent ?? []) : (links?.asChild ?? [])

  const inviteLink = code ? makeFamilyInviteLink(code) : null

  const onGenerate = useCallback(() => {
    setCodeError(null)
    // Master family switch is OFF: generating would contradict the choice.
    if (demo.enabled && !demo.familyEnabled) {
      Toast.show(
        l`Family invitations are turned off — turn them back on above to generate a code.`,
      )
      return
    }
    createRequest.mutate(isParent ? 'parent' : 'child', {
      onSuccess: r => {
        setCode(r.code)
        // When the code was regenerated, start a fresh 10-minute window. The
        // demo expiry toggle also resets so a brand-new code is live.
        setCodeExpiresAt(r.expiresAt)
        if (demo.enabled) setDemoExpired(false)
      },
      onError: e => {
        const err = e as {error?: string; message?: string}
        const notImplemented =
          err.error === 'MethodNotImplemented' ||
          /not implemented/i.test(err.message ?? '')
        setCodeError(
          notImplemented
            ? l`Family linking isn't available on this server yet.`
            : l`Could not create an invite code. Please try again.`,
        )
      },
    })
  }, [createRequest, isParent, demo.enabled, demo.familyEnabled, l])

  const onCopyLink = useCallback(() => {
    if (!inviteLink) return
    // Copying is gated by the same password confirm as sharing, so a link can
    // never be copied away from the account owner's device unnoticed.
    passwordDialog.open(() => {
      void setStringAsync(inviteLink)
      Toast.show(l`Copied to clipboard`, {type: 'success'})
    })
  }, [inviteLink, passwordDialog, l])

  const onShareViaMessage = useCallback(() => {
    if (!inviteLink) return
    // Always confirm the password first — both in real mode and demo mode —
    // so a send can never happen by accident.
    passwordDialog.open(() => shareDialogControl.open())
  }, [inviteLink, passwordDialog, shareDialogControl])

  const familyProfile = useMemo(
    () =>
      profile
        ? {
            did: profile.did,
            handle: profile.handle,
            displayName: profile.displayName,
            avatar: profile.avatar,
          }
        : undefined,
    [profile],
  )

  // The message that gets pre-filled in the chat composer: always includes
  // the sender's profile name so the recipient knows exactly who is asking.
  const shareMessage = useMemo(() => {
    if (!inviteLink) return inviteLink
    const name =
      familyProfile?.displayName ||
      familyProfile?.handle ||
      currentAccount?.handle ||
      ''
    return buildFamilyInviteMessage({
      role: isParent ? 'parent' : 'child',
      name,
      link: inviteLink,
    })
  }, [inviteLink, familyProfile, currentAccount, isParent])

  return (
    <Layout.Screen>
      <Layout.Header.Outer>
        <Layout.Header.BackButton />
        <Layout.Header.Content>
          <Layout.Header.TitleText>
            <Trans>Family</Trans>
          </Layout.Header.TitleText>
        </Layout.Header.Content>
        <Layout.Header.Slot />
      </Layout.Header.Outer>
      <Layout.Content>
        <View style={[a.p_xl, a.gap_xl]}>
          {/* Demo mode — simulate the whole flow with a fake parent/child. */}
          <Toggle.Item
            name="family_demo_mode"
            value={demo.enabled}
            onChange={v => (v ? enableDemo() : disableDemo())}
            label={l`Demo mode`}
            style={[
              a.w_full,
              a.p_md,
              a.rounded_lg,
              a.border,
              t.atoms.border_contrast_low,
              t.atoms.bg_contrast_50,
            ]}>
            <View style={[a.pr_xs]}>
              <CelebrateIcon
                width={24}
                fill={t.atoms.text_contrast_medium.color}
              />
            </View>
            <Toggle.LabelText style={[a.flex_1, a.text_md, a.font_medium]}>
              <Trans>Demo mode</Trans>
            </Toggle.LabelText>
            <Toggle.Platform />
          </Toggle.Item>
          {demo.enabled ? (
            <View style={[a.gap_sm]}>
              <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                <Trans>
                  Try the full family flow with a simulated parent or child —
                  codes, QR and chat are generated locally, no backend needed.
                </Trans>
              </Text>
              <SegmentedControl.Root
                label={l`Demo perspective`}
                type="radio"
                value={demo.role}
                onChange={v => setDemoRole(v)}>
                <SegmentedControl.Item
                  value="learner"
                  label={l`Learner`}>
                  <SegmentedControl.ItemText>
                    <Trans>Learner</Trans>
                  </SegmentedControl.ItemText>
                </SegmentedControl.Item>
                <SegmentedControl.Item
                  value="parent"
                  label={l`Parent`}>
                  <SegmentedControl.ItemText>
                    <Trans>Parent</Trans>
                  </SegmentedControl.ItemText>
                </SegmentedControl.Item>
              </SegmentedControl.Root>
              <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                {demo.role === 'parent' ? (
                  <Trans>
                    Simulating a parent: {demoPeer(demo.role).displayName} will
                    be your demo child in chat.
                  </Trans>
                ) : (
                  <Trans>
                    Simulating a learner: {demoPeer(demo.role).displayName} will
                    be your demo parent in chat.
                  </Trans>
                )}
              </Text>
              <Toggle.Item
                name="family_demo_expired"
                value={demo.expired}
                onChange={setDemoExpired}
                label={l`Simulate an expired invite`}
                style={[
                  a.w_full,
                  a.p_md,
                  a.rounded_lg,
                  a.border,
                  t.atoms.border_contrast_low,
                  t.atoms.bg_contrast_50,
                ]}>
                <View style={[a.pr_xs]}>
                  <CircleInfoIcon
                    width={24}
                    fill={t.atoms.text_contrast_medium.color}
                  />
                </View>
                <Toggle.LabelText style={[a.flex_1, a.text_md, a.font_medium]}>
                  <Trans>Simulate an expired invite</Trans>
                </Toggle.LabelText>
                <Toggle.Platform />
              </Toggle.Item>
              <Button
                label={l`Open demo chat`}
                size="small"
                color="secondary"
                onPress={() => navigation.navigate('DemoConversation')}>
                <ButtonText>
                  <Trans>Open demo chat</Trans>
                </ButtonText>
              </Button>
            </View>
          ) : null}

          {/* Master family switch (demo): OFF blocks every family surface. */}
          {demo.enabled ? (
            <View style={[a.gap_sm]}>
              <Toggle.Item
                name="family_allow_invites"
                value={demo.familyEnabled}
                onChange={v => {
                  if (v) {
                    setFamilyEnabled(true)
                  } else if (demo.accepted) {
                    Toast.show(
                      l`Unlink your linked family members first — then you can turn invitations off.`,
                    )
                  } else {
                    setFamilyEnabled(false)
                  }
                }}
                label={l`Allow family invitations`}
                style={[
                  a.w_full,
                  a.p_md,
                  a.rounded_lg,
                  a.border,
                  t.atoms.border_contrast_low,
                  t.atoms.bg_contrast_50,
                ]}>
                <View style={[a.pr_xs]}>
                  <CircleInfoIcon
                    width={24}
                    fill={t.atoms.text_contrast_medium.color}
                  />
                </View>
                <Toggle.LabelText style={[a.flex_1, a.text_md, a.font_medium]}>
                  <Trans>Allow family invitations</Trans>
                </Toggle.LabelText>
                <Toggle.Platform />
              </Toggle.Item>
              {demo.accepted ? (
                <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                  <Trans>
                    You have a linked member — remove them first before turning
                    invitations off.
                  </Trans>
                </Text>
              ) : null}
              {demo.familyEnabled ? (
                <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                  <Trans>
                    When on, family invitations can be sent to you and accepted
                    by you. Turn it off to stop receiving invitations.
                  </Trans>
                </Text>
              ) : null}
            </View>
          ) : null}

          {/* Master family switch OFF (demo): every feature below is disabled
              until the switch goes back on — the screen shows the off state
              instead of the invite UI, matching the Hub CTA and the chat. */}
          {demo.enabled && !demo.familyEnabled ? (
            <View
              style={[
                a.align_center,
                a.gap_sm,
                a.py_xl,
                a.px_xl,
                a.rounded_lg,
                a.border,
                t.atoms.border_contrast_low,
                t.atoms.bg_contrast_25,
              ]}>
              <CircleInfoIcon
                width={28}
                fill={t.atoms.text_contrast_medium.color}
              />
              <Text style={[a.text_lg, a.font_bold, a.text_center]}>
                <Trans>Family invitations are off</Trans>
              </Text>
              <Text style={[a.text_md, a.text_center, a.leading_snug]}>
                <Trans>
                  No invitations can be sent or accepted right now. Turn the
                  switch back on above to use Family again.
                </Trans>
              </Text>
            </View>
          ) : (
            <>
          {/* Preview card — the exact cover the invite shows everywhere. */}
          {familyProfile ? (
            <FamilyInviteCard
              profile={familyProfile}
              role={isParent ? 'parent' : 'child'}
            />
          ) : (
            <View style={[a.align_center, a.py_lg]}>
              <ActivityIndicator />
            </View>
          )}

          <View style={[a.gap_sm]}>
            <Text style={[a.text_2xl, a.font_bold]}>
              {isParent ? (
                <Trans>Add a child</Trans>
              ) : (
                <Trans>Invite a parent</Trans>
              )}
            </Text>
            <Text style={[a.text_md, a.leading_snug]}>
              {isParent ? (
                <Trans>
                  Generate a code to share with your child. Once they accept,
                  you can support their learning and suggest capsules to
                  replay.
                </Trans>
              ) : (
                <Trans>
                  Generate a code to share with a parent. Once they accept,
                  they can support you and suggest capsules to replay.
                </Trans>
              )}
            </Text>
          </View>

          <Toggle.Item
            name="family_generate_code"
            value={false}
            onChange={onGenerate}
            label={l`Generate a code`}
            style={[
              a.w_full,
              a.p_md,
              a.rounded_lg,
              a.border,
              t.atoms.border_contrast_low,
              t.atoms.bg_contrast_50,
            ]}>
            <View style={[a.pr_xs]}>
              <QrCodeIcon
                width={24}
                fill={t.atoms.text_contrast_medium.color}
              />
            </View>
            <Toggle.LabelText style={[a.flex_1, a.text_md, a.font_medium]}>
              {code ? (
                <Trans>Generate a new code</Trans>
              ) : (
                <Trans>Generate a code</Trans>
              )}
            </Toggle.LabelText>
            <Toggle.Platform />
          </Toggle.Item>
          {createRequest.isPending && <ActivityIndicator />}
          {codeError ? (
            <Text style={[{color: t.palette.negative_400}, a.text_sm]}>
              {codeError}
            </Text>
          ) : null}

          {code && inviteLink ? (
            <InviteCodePanel
              link={inviteLink}
              expiresAt={codeExpiresAt}
              demoExpired={demo.enabled && demo.expired}
              onRegenerate={onGenerate}
              onCopyLink={onCopyLink}
              onShareViaMessage={onShareViaMessage}
            />
          ) : null}

          <View style={[a.gap_sm]}>
            <Text style={[a.text_lg, a.font_bold]}>
              {isParent ? (
                <Trans>Your children</Trans>
              ) : (
                <Trans>Your parents</Trans>
              )}
            </Text>
            {isLoading ? (
              <ActivityIndicator />
            ) : members.length === 0 ? (
              <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
                {isParent ? (
                  <Trans>No children linked yet.</Trans>
                ) : (
                  <Trans>No parents linked yet.</Trans>
                )}
              </Text>
            ) : (
              <View style={[a.gap_sm]}>
                {members.map(m => (
                  <MemberRow
                    key={m.peerDid}
                    peerDid={m.peerDid}
                    showProgress={isParent}
                  />
                ))}
              </View>
            )}
          </View>
          {demo.enabled && !demo.accepted ? (
            <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
              <Trans>
                Tip: open Chats and accept the invitation from{' '}
                {demoPeer(demo.role).displayName} to see them appear here.
              </Trans>
            </Text>
          ) : null}

          <View style={[a.gap_sm]}>
            <Button
              label={l`Enter a code`}
              size="small"
              color="secondary"
              onPress={() => setShowEnterCode(v => !v)}>
              <ButtonText>
                <Trans>Enter a code</Trans>
              </ButtonText>
            </Button>
            {showEnterCode ? <EnterCodePanel /> : null}
          </View>
            </>
          )}
        </View>
      </Layout.Content>

      <PasswordConfirmDialog control={passwordDialog.control} />
      <FamilyShareDialog
        control={shareDialogControl}
        link={inviteLink ?? ''}
        code={code}
        message={shareMessage ?? undefined}
      />
    </Layout.Screen>
  )
}

function MemberRow({
  peerDid,
  showProgress,
}: {
  peerDid: string
  showProgress: boolean
}) {
  const t = useTheme()
  const demo = useDemoFamily()
  const {data: profile} = useProfileQuery({did: peerDid})

  // Demo peers have no real profile — use the simulated one.
  const demoPeerProfile =
    demo.enabled && demoPeer(demo.role).did === peerDid
      ? demoPeer(demo.role)
      : undefined

  return (
    <View
      style={[
        a.border,
        a.rounded_md,
        a.p_lg,
        a.gap_sm,
        t.atoms.border_contrast_low,
      ]}>
      <View style={[a.flex_row, a.justify_between, a.align_center, a.gap_sm]}>
        <Text style={[a.text_md, a.font_bold, a.flex_1]} numberOfLines={1}>
          {demoPeerProfile?.displayName ||
            profile?.displayName ||
            profile?.handle ||
            peerDid.slice(0, 14)}
        </Text>
        <RemoveLinkButton peerDid={peerDid} />
      </View>
      {showProgress ? <ChildProgress childDid={peerDid} /> : null}
    </View>
  )
}

/** Per-child play summary (moved from the parent Hub). */
function ChildProgress({childDid}: {childDid: string}) {
  const {t: l} = useLingui()
  const demo = useDemoFamily()

  // Demo mode: render a simulated progress summary for the demo child. All
  // hooks run unconditionally (the demo plays query resolves to [] instantly)
  // so hook order stays stable.
  const isDemoChild = demo.enabled && demoPeer(demo.role).did === childDid
  const {data: plays, isLoading: playsLoading} =
    useFamilyMemberPlaysQuery(childDid)
  const capsuleUris = useMemo(
    () => (plays ?? []).map(p => p.capsuleUri),
    [plays],
  )
  const {data: capsuleByUri, isLoading: capsulesLoading} =
    useCapsulesByUrisQuery(capsuleUris)
  const sections = useMemo(
    () => deriveLearnerSections(plays ?? [], capsuleByUri ?? {}),
    [plays, capsuleByUri],
  )

  if (isDemoChild) {
    return (
      <View style={[a.gap_xs]}>
        <Text style={[a.text_sm, {opacity: 0.7}]}>
          {l`2 in progress · 1 to boost · 4 completed`}
        </Text>
        <View style={[a.gap_xs]}>
          <Text style={[a.text_sm, a.font_bold]}>
            {l`Suggest a replay to boost`}
          </Text>
          <Text style={[a.text_sm, {opacity: 0.85}]}>
            • {l`English for Beginners`} — 42%
          </Text>
          <Text style={[a.text_sm, {opacity: 0.85}]}>
            • {l`Numbers and Colors`} — 36%
          </Text>
        </View>
      </View>
    )
  }

  if (playsLoading || capsulesLoading) {
    return <ActivityIndicator />
  }
  return (
    <View style={[a.gap_xs]}>
      <Text style={[a.text_sm, {opacity: 0.7}]}>
        {l`${sections.continueItems.length} in progress · ${sections.boostItems.length} to boost · ${sections.historyItems.length} completed`}
      </Text>
      {sections.boostItems.length > 0 && (
        <View style={[a.gap_xs]}>
          <Text style={[a.text_sm, a.font_bold]}>
            {l`Suggest a replay to boost`}
          </Text>
          {sections.boostItems.slice(0, 3).map(item => (
            <Text key={item.capsule.uri} style={[a.text_sm, {opacity: 0.85}]}>
              • {item.capsule.title} — {item.pct}%
            </Text>
          ))}
        </View>
      )}
      {sections.boostItems.length === 0 && (
        <Text style={[a.text_sm, {opacity: 0.6}]}>
          {l`No finished capsules under 50% right now.`}
        </Text>
      )}
    </View>
  )
}

/**
 * The generated-invite panel under the QR: a LIVE expiry countdown replaces
 * the raw code text (the code lives inside the QR and the shared link, so a
 * long string on screen adds noise). Ticks every second, turns amber under a
 * minute and red at zero; when the code is genuinely past its TTL (or the demo
 * "expired" toggle simulates it) the whole panel collapses to a calm expired
 * notice with a regenerate action — no QR, no copy, no share.
 */
function InviteCodePanel({
  link,
  expiresAt,
  demoExpired,
  onRegenerate,
  onCopyLink,
  onShareViaMessage,
}: {
  link: string
  expiresAt: string | null
  demoExpired: boolean
  onRegenerate: () => void
  onCopyLink: () => void
  onShareViaMessage: () => void
}) {
  const {t: l} = useLingui()
  const t = useTheme()
  // Ticks each second so the countdown is live (Date.now lives only inside
  // this effect callback, keeping the render pure).
  const [nowMs, setNowMs] = useState<number | null>(null)
  useEffect(() => {
    if (!expiresAt) return
    const tick = () => setNowMs(Date.now())
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [expiresAt])

  const deadlineMs = expiresAt ? new Date(expiresAt).getTime() : null
  const remainingMs =
    nowMs != null && deadlineMs != null ? Math.max(0, deadlineMs - nowMs) : null
  // Expired when the clock ran out OR the demo toggle simulates an expired
  // code (a fresh code resets that toggle via onGenerate).
  const isExpired = demoExpired || (remainingMs != null && remainingMs <= 0)

  if (isExpired) {
    return (
      <View
        style={[
          a.w_full,
          a.p_lg,
          a.rounded_md,
          a.border,
          a.gap_sm,
          t.atoms.border_contrast_low,
          t.atoms.bg_contrast_25,
        ]}>
        <Text style={[a.text_md, a.font_bold]}>
          <Trans>This invitation has expired</Trans>
        </Text>
        <Text style={[a.text_sm, t.atoms.text_contrast_medium]}> 
          {demoExpired ? (
            <Trans>
              Codes expire 10 minutes after they're created. Turn off
              "Simulate an expired invite" and generate a new code to share
              again.
            </Trans>
          ) : (
            <Trans>
              Codes expire 10 minutes after they're created. Generate a new
              code to share again.
            </Trans>
          )}
        </Text>
        <View style={[a.flex_row, a.justify_center, a.pt_xs]}>
          <Button
            label={l`Generate a new code`}
            size="small"
            color="primary"
            onPress={onRegenerate}>
            <ButtonIcon icon={QrCodeIcon} />
            <ButtonText>
              <Trans>Generate a new code</Trans>
            </ButtonText>
          </Button>
        </View>
      </View>
    )
  }

  // Live expiry display for the pill + a11y label.
  const totalSec =
    remainingMs != null ? Math.max(0, Math.ceil(remainingMs / 1000)) : null
  const mm = totalSec != null ? Math.floor(totalSec / 60) : null
  const ss = totalSec != null ? String(totalSec % 60).padStart(2, '0') : null
  const display =
    mm != null && ss != null ? `${mm}:${ss}` : null
  const urgent = totalSec != null && totalSec <= 60
  const countdownColor = urgent
    ? t.palette.negative_400
    : t.atoms.text_contrast_medium.color

  return (
    <View style={[a.gap_md, a.align_center]}>
      <Text
        style={[
          a.text_sm,
          t.atoms.text_contrast_medium,
          a.text_center,
        ]}>
        <Trans>
          Scan it or share the link — it opens the invitation instantly and
          works only once.
        </Trans>
      </Text>
      <View
        style={[
          a.rounded_lg,
          a.overflow_hidden,
          a.border,
          t.atoms.border_contrast_low,
        ]}>
        <FamilyQrCode link={link} />
      </View>
      {/* Live expiry countdown — the code itself is in the QR/link, so a
          raw code string is replaced by how much time is left. */}
      <View
        style={[
          a.flex_row,
          a.align_center,
          a.justify_center,
          a.gap_xs,
          a.rounded_full,
          a.border,
          a.px_lg,
          a.py_xs,
          t.atoms.border_contrast_low,
          t.atoms.bg_contrast_25,
        ]}
        accessibilityRole="text"
        accessibilityLabel={
          display ? l`Invitation expires in ${display}` : l`Invitation expiry`
        }
        accessibilityHint={l`Time remaining before the invitation link stops working`}>
        <ClockIcon width={16} fill={countdownColor} />
        <Text
          style={[
            a.text_md,
            a.font_bold,
            {
              color: countdownColor,
              fontVariant: ['tabular-nums'],
            },
          ]}>
          {display ? `Expires in ${display}` : '…'}
        </Text>
      </View>
      <Text style={[a.text_sm, t.atoms.text_contrast_medium, a.text_center]}>
        <Trans>Codes are valid for 10 minutes.</Trans>
      </Text>
      <View
        style={[a.flex_row, a.gap_sm, a.flex_wrap, a.justify_center]}>
        <Button
          label={l`Copy invitation link`}
          size="small"
          color="secondary"
          onPress={onCopyLink}>
          <ButtonIcon icon={ChainLinkIcon} />
          <ButtonText>
            <Trans>Copy invitation link</Trans>
          </ButtonText>
        </Button>
        <Button
          label={l`Share via message`}
          size="small"
          color="primary"
          onPress={onShareViaMessage}>
          <ButtonIcon icon={QrCodeIcon} />
          <ButtonText>
            <Trans>Share via message</Trans>
          </ButtonText>
        </Button>
      </View>
    </View>
  )
}
